// Pure geometry helpers shared by the editor, the print renderer and the legacy converter.

export type LngLat = [number, number];
/** [lng, lat, elevation in metres | null] */
export type Coord = [number, number, number | null];
export type Bounds = [west: number, south: number, east: number, north: number];

const TILE = 512; // Mapbox GL world size at zoom 0, in CSS px
const R = 6371008.8;
const rad = (d: number) => (d * Math.PI) / 180;

export const mercX = (lng: number) => ((lng + 180) / 360) * TILE;
export const mercY = (lat: number) => {
  const s = Math.max(-0.9999, Math.min(0.9999, Math.sin(rad(lat))));
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE;
};
const lngFromX = (x: number) => (x / TILE) * 360 - 180;
const latFromY = (y: number) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / TILE))) * 180) / Math.PI;

export function boundsOf(lines: ReadonlyArray<ReadonlyArray<readonly [number, number, ...unknown[]]>>): Bounds | null {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const line of lines) {
    for (const c of line) {
      if (c[0] < w) w = c[0];
      if (c[0] > e) e = c[0];
      if (c[1] < s) s = c[1];
      if (c[1] > n) n = c[1];
    }
  }
  return Number.isFinite(w) ? [w, s, e, n] : null;
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Centre and zoom that fit `bounds` inside a viewport of `width` x `height` CSS px,
 * leaving `padding` clear. Same maths as Mapbox GL's fitBounds (north-up).
 */
export function fitCamera(bounds: Bounds, width: number, height: number, padding: Padding, maxZoom = 16) {
  const [w, s, e, n] = bounds;
  const x0 = mercX(w), x1 = mercX(e), y0 = mercY(n), y1 = mercY(s);
  const availW = Math.max(1, width - padding.left - padding.right);
  const availH = Math.max(1, height - padding.top - padding.bottom);
  const dx = Math.max(x1 - x0, 1e-9);
  const dy = Math.max(y1 - y0, 1e-9);
  const zoom = Math.min(maxZoom, Math.log2(Math.min(availW / dx, availH / dy)));
  const scale = 2 ** zoom;
  // The padded box's centre is offset from the viewport centre; shift the map centre to match.
  const cx = (x0 + x1) / 2 + (padding.right - padding.left) / 2 / scale;
  const cy = (y0 + y1) / 2 + (padding.bottom - padding.top) / 2 / scale;
  return { center: [lngFromX(cx), latFromY(cy)] as LngLat, zoom };
}

export function haversine(a: readonly [number, number, ...unknown[]], b: readonly [number, number, ...unknown[]]) {
  const dLat = rad(b[1] - a[1]);
  const dLng = rad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function lineDistance(coords: ReadonlyArray<Coord>) {
  let d = 0;
  for (let i = 1; i < coords.length; i++) d += haversine(coords[i - 1], coords[i]);
  return d;
}

/**
 * Total climb in metres. GPS elevation is noisy, so only count a rise once it
 * exceeds `threshold` above the last counted level.
 */
export function elevationGain(coords: ReadonlyArray<Coord>, threshold = 3) {
  let gain = 0;
  let ref: number | null = null;
  for (const c of coords) {
    const ele = c[2];
    if (ele == null || !Number.isFinite(ele)) continue;
    if (ref == null) {
      ref = ele;
    } else if (ele > ref + threshold) {
      gain += ele - ref;
      ref = ele;
    } else if (ele < ref - threshold) {
      ref = ele;
    }
  }
  return gain;
}

/** Douglas-Peucker in the Mercator plane; `tolerance` is in zoom-0 world px. Iterative, safe for long tracks. */
export function simplify<T extends readonly [number, number, ...unknown[]]>(coords: ReadonlyArray<T>, tolerance: number): T[] {
  const n = coords.length;
  if (n <= 2) return coords.slice();
  const xs = new Float64Array(n);
  const ys = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    xs[i] = mercX(coords[i][0]);
    ys[i] = mercY(coords[i][1]);
  }
  const keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  const tol2 = tolerance * tolerance;
  const stack: number[] = [0, n - 1];
  while (stack.length) {
    const last = stack.pop()!;
    const first = stack.pop()!;
    let maxD = 0;
    let idx = -1;
    const ax = xs[first], ay = ys[first];
    const bx = xs[last] - ax, by = ys[last] - ay;
    const len2 = bx * bx + by * by;
    for (let i = first + 1; i < last; i++) {
      const px = xs[i] - ax, py = ys[i] - ay;
      let t = len2 ? (px * bx + py * by) / len2 : 0;
      t = Math.max(0, Math.min(1, t));
      const ex = px - t * bx, ey = py - t * by;
      const d = ex * ex + ey * ey;
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx !== -1 && maxD > tol2) {
      keep[idx] = 1;
      stack.push(first, idx, idx, last);
    }
  }
  const out: T[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(coords[i]);
  return out;
}

/** Roughly 1.5 m at the equator, in zoom-0 world px: invisible at print size. */
export const PRINT_TOLERANCE = (1.5 / 40075016.686) * TILE;

/** Simplify a track for storage, loosening the tolerance until it is under `maxPoints`. */
export function simplifyForStorage<T extends readonly [number, number, ...unknown[]]>(coords: ReadonlyArray<T>, maxPoints = 12000): T[] {
  let tol = PRINT_TOLERANCE;
  let out = simplify(coords, tol);
  while (out.length > maxPoints) {
    tol *= 1.8;
    out = simplify(coords, tol);
  }
  return out;
}

/**
 * Simplify a set of tracks together so their combined point count stays under `budget`,
 * loosening one shared tolerance until it does. Keeps a very long multi-file route
 * (weeks of 1-second recordings) to a size that saves and loads quickly; 40,000 points is
 * still far finer than the largest print can show.
 */
export function fitPointBudget<T extends readonly [number, number, ...unknown[]]>(tracks: ReadonlyArray<ReadonlyArray<T>>, budget = 40000): T[][] {
  let out = tracks.map((t) => t.slice());
  let tol = PRINT_TOLERANCE;
  while (out.reduce((n, t) => n + t.length, 0) > budget) {
    tol *= 1.6;
    out = out.map((t) => simplify(t, tol));
  }
  return out;
}

/** Elevation at `n` points evenly spaced by distance along a track, or null if it has no elevation. */
export function sampleElevation(coords: ReadonlyArray<Coord>, n: number): number[] | null {
  const pts = coords.filter((c) => c[2] != null);
  if (pts.length < 2) return null;
  const cum = new Float64Array(pts.length);
  for (let i = 1; i < pts.length; i++) cum[i] = cum[i - 1] + haversine(pts[i - 1], pts[i]);
  const total = cum[pts.length - 1];
  if (!(total > 0)) return null;
  const out: number[] = [];
  let j = 0;
  for (let i = 0; i < n; i++) {
    const target = (total * i) / (n - 1);
    while (j < pts.length - 2 && cum[j + 1] < target) j++;
    const span = cum[j + 1] - cum[j];
    const t = span > 0 ? Math.max(0, Math.min(1, (target - cum[j]) / span)) : 0;
    out.push(round((pts[j][2] as number) + ((pts[j + 1][2] as number) - (pts[j][2] as number)) * t, 1));
  }
  return out;
}

export interface TrackMeasure {
  /** metres */
  distance: number;
  /** metres climbed */
  gain: number;
  /** Elevation samples evenly spaced by distance, for the profile. */
  profile: number[] | null;
}

export const PROFILE_SAMPLES = 240;

/**
 * Distance, climb and elevation profile of a track. Taken from the full recording when a
 * route is imported and stored with it, so the figures never depend on how much the drawn
 * line is simplified afterwards.
 */
export function measureTrack(coords: ReadonlyArray<Coord>): TrackMeasure {
  return { distance: round(lineDistance(coords), 1), gain: round(elevationGain(coords), 1), profile: sampleElevation(coords, PROFILE_SAMPLES) };
}

export const round = (v: number, dp: number) => {
  const f = 10 ** dp;
  return Math.round(v * f) / f;
};
